import React, { useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Handshake, Check, X, Link2, FileText, ChevronRight, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { CreateAgentDialog, AgentDetailDialog, openSsm } from "@/components/AgentDialogs";
import { TIER_LABEL, TIER_CLS } from "@/components/ProductVariants";
import { WhatsAppButton } from "@/components/WhatsAppButton";

export const AgentApprovals = ({ agents, isAdmin, reload }) => {
  const [selected, setSelected] = useState(null);
  const pending = agents.filter((a) => !a.approved);
  const approved = agents.filter((a) => a.approved);
  const portalUrl = `${window.location.origin}/agent/login`;

  const setApproved = async (a, approved) => {
    await api.patch(`/users/${a.id}`, { approved });
    toast.success(approved ? `${a.name} approved` : `${a.name} revoked`);
    reload();
  };
  const [toDelete, setToDelete] = useState(null);
  const deleteAgent = async () => {
    const a = toDelete;
    try {
      const { data } = await api.delete(`/users/${a.id}`);
      toast.success(`${a.company || a.name} deleted${data.jobs ? ` • ${data.jobs} job(s) kept in history` : ""}`);
      setToDelete(null); setSelected(null); reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to delete agent"); }
  };

  const copyLink = () => { navigator.clipboard.writeText(portalUrl); toast.success("Agent Portal link copied"); };
  const stop = (fn) => (e) => { e.stopPropagation(); fn(); };

  const Row = ({ a }) => (
    <Card className="p-3 flex items-center justify-between flex-wrap gap-2 cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => setSelected(a)} data-testid={`agent-row-${a.email}`}>
      <div className="text-sm min-w-0">
        <div className="font-semibold flex items-center gap-1">{a.company || a.name} <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" /></div>
        <div className="text-xs text-muted-foreground">PIC: {a.name} {a.phone && `• ${a.phone}`} • {a.email}</div>
        {a.ssm_no && <div className="text-xs text-muted-foreground font-mono">SSM: {a.ssm_no}</div>}
      </div>
      <div className="flex items-center gap-2">
        {a.phone && <WhatsAppButton size="icon" phone={a.phone} message={`Hi ${a.name}, this is Rozfa Printing.`} testid={`wa-agent-${a.email}`} />}
        {a.ssm_file && <Button size="sm" variant="ghost" onClick={stop(() => openSsm(a))} data-testid={`view-ssm-${a.email}`}><FileText className="w-3 h-3 mr-1" /> SSM Cert</Button>}
        <Badge className={TIER_CLS[a.tier || "normal"]}>{TIER_LABEL[a.tier || "normal"]}</Badge>
        {a.approved ? <Badge className="bg-emerald-600">Approved</Badge> : <Badge variant="secondary">Pending</Badge>}
        {isAdmin && (a.approved
          ? <Button size="sm" variant="outline" onClick={stop(() => setApproved(a, false))} data-testid={`revoke-agent-${a.email}`}><X className="w-3 h-3 mr-1" /> Revoke</Button>
          : <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={stop(() => setApproved(a, true))} data-testid={`approve-agent-${a.email}`}><Check className="w-3 h-3 mr-1" /> Approve</Button>)}
        {isAdmin && <Button size="sm" variant="ghost" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" onClick={stop(() => setToDelete(a))} data-testid={`delete-agent-${a.email}`}><Trash2 className="w-3.5 h-3.5" /></Button>}
      </div>
    </Card>
  );

  return (
    <div className="space-y-3" data-testid="agent-approvals">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-display font-bold flex items-center gap-2"><Handshake className="w-5 h-5" /> Rozfa Agents</h2>
          <p className="text-sm text-muted-foreground">{pending.length} pending approval • {approved.length} active • click an agent to view details</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && <CreateAgentDialog reload={reload} />}
          <Button variant="outline" size="sm" onClick={copyLink} data-testid="copy-agent-portal-link"><Link2 className="w-4 h-4 mr-1" /> Copy Agent Portal Link</Button>
        </div>
      </div>
      {pending.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs font-mono uppercase text-amber-600">Pending Approval</div>
          {pending.map((a) => <Row key={a.id} a={a} />)}
        </div>
      )}
      <div className="space-y-2">
        <div className="text-xs font-mono uppercase text-muted-foreground">Active Agents</div>
        {approved.length === 0 && <div className="text-sm text-muted-foreground">No active agents</div>}
        {approved.map((a) => <Row key={a.id} a={a} />)}
      </div>
      <AgentDetailDialog agent={agents.find((a) => a.id === selected?.id) || selected} open={!!selected} onClose={() => setSelected(null)} isAdmin={isAdmin} reload={reload} />
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-agent-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete agent {toDelete?.company || toDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              The agent account ({toDelete?.email}) will be removed and can no longer log in to the Agent Portal. Existing jobs submitted by this agent are kept for records. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-agent-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteAgent} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="delete-agent-confirm">Delete Agent</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
